// src/components/TaskComentarios.jsx
import React, { useState, useEffect, useRef } from "react";
import { FiUser, FiCalendar } from "react-icons/fi";
import { IoReturnUpBack } from "react-icons/io5";
import { FaTrashAlt, FaSmile, FaCamera, FaPaperclip } from "react-icons/fa"
import { formatText } from "../utils/formatText";
import TaskReactions from "./TaskReactions";
import "./Task.css";
import ComentariosSkeleton from "./skeletons/ComentariosSkeleton";



// Helper de sanitização para nomes de arquivos
const sanitizeFileName = (name) => name
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_')
  .trim().replace(/^_+|_+$/g, '');

// Componente de imagens com carrossel (acima do texto)
const ComentarioImagens = ({ anexos, userId, onRemover, setImagemAmpliada }) => {
  const [indexAtual, setIndexAtual] = useState(0);
  const imagens = anexos.filter(a => a.mime_type?.startsWith('image/'));

  if (imagens.length === 0) return null;

  const proxima = (e) => {
    e.stopPropagation();
    setIndexAtual(prev => (prev + 1) % imagens.length);
  };
  const anterior = (e) => {
    e.stopPropagation();
    setIndexAtual(prev => (prev - 1 + imagens.length) % imagens.length);
  };

  return (
    <div className="comentario-imagem-carrossel">
      <img
        src={imagens[indexAtual].file_url}
        alt={imagens[indexAtual].file_name}
        onClick={() => setImagemAmpliada?.({ index: indexAtual, imagens })}
        className="comentario-imagem-grande"
      />
      {imagens[indexAtual].user_id === userId && (
        <button
          className="botao-lixeira-comentario-img"
          onClick={(e) => { e.stopPropagation(); onRemover(imagens[indexAtual].id, imagens[indexAtual].file_url); }}
          title="Excluir imagem"
        >
          <FaTrashAlt size={13} />
        </button>
      )}
      {imagens.length > 1 && (
        <>
          <button className="carrossel-seta esquerda" onClick={anterior}>‹</button>
          <button className="carrossel-seta direita" onClick={proxima}>›</button>
          <div className="carrossel-indicadores">
            {imagens.map((_, i) => (
              <span
                key={i}
                className={`carrossel-dot ${i === indexAtual ? 'ativo' : ''}`}
                onClick={(e) => { e.stopPropagation(); setIndexAtual(i); }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Componente de arquivos (não-imagem) - abaixo do texto
const ComentarioArquivos = ({ anexos, userId, onRemover }) => {
  const arquivos = anexos.filter(a => !a.mime_type?.startsWith('image/'));
  if (arquivos.length === 0) return null;

  return (
    <div className="anexos-lista comentario-arquivos-lista">
      {arquivos.map(anexo => (
        <div key={anexo.id} className="anexo-item">
          <a href={anexo.file_url} target="_blank" rel="noopener noreferrer">
            {anexo.file_name}
          </a>
          {anexo.user_id === userId && (
            <button type="button" title="Remover" onClick={() => onRemover(anexo.id, anexo.file_url)}>×</button>
          )}
        </div>
      ))}
    </div>
  );
};

const MencoesTooltip = ({ children, userId, projetoAtual, containerId, supabaseClient }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [loadingTooltip, setLoadingTooltip] = useState(false);

  useEffect(() => {
    if (!showTooltip || tooltipData || loadingTooltip || !userId) return;

    const fetchTooltipData = async () => {
      setLoadingTooltip(true);
      try {
        const { data: profile, error: profileError } = await supabaseClient
          .from("profiles")
          .select("nome, nickname, funcao, container, avatar_url")
          .eq("id", userId)
          .single();

        if (profileError || !profile) {
          setTooltipData({ error: true });
          return;
        }

        let pertenceA = "Não pertence a nenhuma entidade";

        const { data: projectMembers } = await supabaseClient
          .from("project_members")
          .select("project_id")
          .eq("user_id", userId);

        if (Array.isArray(projectMembers) && projectMembers.length > 0) {
          const projectIds = projectMembers.map(pm => pm.project_id);
          const { data: projectNames } = await supabaseClient
            .from("projects")
            .select("name")
            .in("id", projectIds)
            .limit(1);
          if (projectNames?.[0]?.name) {
            pertenceA = `Membro do Projeto "${projectNames[0].name}"`;
          }
        } else {
          const { data: setorMembers } = await supabaseClient
            .from("setor_members")
            .select("setor_id")
            .eq("user_id", userId);
          if (Array.isArray(setorMembers) && setorMembers.length > 0) {
            const setorIds = setorMembers.map(sm => sm.setor_id);
            const { data: setorNames } = await supabaseClient
              .from("setores")
              .select("name")
              .in("id", setorIds)
              .limit(1);
            if (setorNames?.[0]?.name) {
              pertenceA = `Membro do Setor "${setorNames[0].name}"`;
            }
          }
        }

        setTooltipData({
          nome: profile.nickname || profile.nome || "Usuário",
          funcao: profile.funcao || "Função não informada",
          container_id: profile.container || "—",
          pertenceA,
          avatar_url: profile.avatar_url,
        });
      } catch (err) {
        console.error("Erro ao carregar tooltip de menção:", err);
        setTooltipData({ error: true });
      } finally {
        setLoadingTooltip(false);
      }
    };

    fetchTooltipData();
  }, [showTooltip, userId, tooltipData, loadingTooltip, supabaseClient]);

  if (!userId) {
    return (
      <span style={{ color: "#1E88E5", textDecoration: "underline", fontWeight: "bold", cursor: "default" }}>
        {children}
      </span>
    );
  }

  return (
    <span
      style={{
        position: "relative",
        color: "#1E88E5",
        textDecoration: "underline",
        cursor: "pointer",
        fontWeight: "bold",
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (
        <div
          className="mencoes-tooltip"
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: "8px",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "6px",
            padding: "10px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            zIndex: 1000,
            width: "220px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            fontSize: "14px",
          }}
        >
          {loadingTooltip ? (
            <span>Carregando...</span>
          ) : tooltipData?.error ? (
            <span>Usuário não encontrado</span>
          ) : tooltipData ? (
            <>
              {tooltipData.avatar_url ? (
                <img
                  src={tooltipData.avatar_url}
                  alt={tooltipData.nome}
                  style={{ width: "40px", height: "40px", borderRadius: "50%", marginBottom: "6px" }}
                />
              ) : (
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "6px",
                    fontWeight: "bold",
                    color: "#555",
                  }}
                >
                  {(tooltipData.nome || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <strong>{tooltipData.nome}</strong>
              <div>{tooltipData.funcao}</div>
              <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                Container ID: {tooltipData.container_id}
              </div>
              <div style={{ fontSize: "12px", color: "#555", marginTop: "4px", textAlign: "center" }}>
                {tooltipData.pertenceA}
              </div>
            </>
          ) : null}
        </div>
      )}
    </span>
  );
};

const renderTextoFormatadoComMencoes = (
  conteudo,
  perfilesPorId,
  projetoAtual,
  containerId,
  supabaseClient
) => {
  if (!conteudo) return conteudo;
  const regex = /(@[\p{L}\p{N}_-]+)/gu;
  const partes = conteudo.split(regex).filter(Boolean);
  const elementos = partes.map((parte, index) => {
    if (parte.startsWith("@")) {
      const nickname = parte.slice(1);
      const usuario = Object.values(perfilesPorId).find(
        (p) => p && p.id && (p.nickname === nickname || p.nome === nickname)
      );
      if (usuario && usuario.id) {
        return (
          <MencoesTooltip
            key={index}
            userId={usuario.id}
            projetoAtual={projetoAtual}
            containerId={containerId}
            supabaseClient={supabaseClient}
          >
            {parte}
          </MencoesTooltip>
        );
      } else {
        return (
          <span key={index} style={{ color: "#1E88E5", textDecoration: "underline" }}>
            {parte}
          </span>
        );
      }
    } else {
      return formatText(parte);
    }
  });
  return <>{elementos}</>;
};

const mencionaUsuario = (conteudo, userProfile) => {
  if (!userProfile || !conteudo) return false;
  const termos = [userProfile.nickname, userProfile.nome].filter(Boolean);
  const regex = /@([\p{L}\p{N}_-]+)/gu;
  const mencoes = [...conteudo.matchAll(regex)].map(m => m[1]);
  return mencoes.some(m => termos.includes(m));
};

// ✅ FUNÇÃO CORRIGIDA: Notificar apenas pessoas envolvidas na tarefa
const notificarPessoasEnvolvidasNaTarefa = async ({
  conteudo,
  notaId,
  autorId,
  autorProfile,
  projetoAtual,
  nomeTarefa,
  supabaseClient,
}) => {
  try {
    console.log("🔔 Iniciando notificação para tarefa:", notaId);
    console.log("👤 Autor do comentário:", autorId, autorProfile?.nickname || autorProfile?.nome);
    
    const notificacoesParaEnviar = [];
    const destinatariosProcessados = new Set();

    // 1️⃣ Buscar pessoas MENCIONADAS no comentário
    const mencoes = conteudo.match(/@(\S+)/g);
    const mencionadosSet = new Set();
    
    if (mencoes?.length > 0) {
      const nomesMencionados = mencoes.map(m => m.slice(1));
      console.log("📝 Menções encontradas:", nomesMencionados);
      
      const { data: candidatos, error: mencaoError } = await supabaseClient
        .from("profiles")
        .select("id, nickname, nome")
        .or(
          `nickname.in.(${nomesMencionados.map(n => `"${n}"`).join(",")}),nome.in.(${nomesMencionados.map(n => `"${n}"`).join(",")})`
        );
      
      if (mencaoError) {
        console.error("❌ Erro ao buscar mencionados:", mencaoError);
      } else {
        console.log("✅ Usuários mencionados encontrados:", candidatos?.length || 0);
        (candidatos || []).forEach(p => mencionadosSet.add(p.id));
      }
    } else {
      console.log("📝 Nenhuma menção encontrada no comentário");
    }

    // Enviar notificações para MENCIONADOS
    for (const mencionadoId of mencionadosSet) {
      if (mencionadoId === autorId) {
        console.log("⏭️ Pulando autor nas menções:", mencionadoId);
        continue;
      }
      if (destinatariosProcessados.has(mencionadoId)) {
        console.log("⏭️ Usuário já processado:", mencionadoId);
        continue;
      }

      console.log("➕ Adicionando notificação de MENÇÃO para:", mencionadoId);
      const mensagem = `${autorProfile?.nickname || autorProfile?.nome || "Alguém"} mencionou você em um comentário na tarefa "${nomeTarefa}".`;
      
      notificacoesParaEnviar.push({
        user_id: mencionadoId,
        remetente_id: autorId,
        nota_id: notaId,
        projeto_id: projetoAtual?.id || null,
        tipo: "menção",
        mensagem,
        lido: false,
      });

      destinatariosProcessados.add(mencionadoId);
    }

    // 2️⃣ Buscar RESPONSÁVEIS ATUAIS da tarefa (nota_responsaveis)
    console.log("🔍 Buscando responsáveis da tarefa:", notaId);
    const { data: responsaveis, error: respError } = await supabaseClient
      .from("nota_responsaveis")
      .select("usuario_id")
      .eq("nota_id", notaId);

    if (respError) {
      console.error("❌ Erro ao buscar responsáveis:", respError);
    } else {
      console.log("✅ Responsáveis encontrados:", responsaveis?.length || 0, responsaveis);
    }

    const responsaveisIds = responsaveis?.map(r => r.usuario_id).filter(Boolean) || [];
    console.log("📋 IDs dos responsáveis:", responsaveisIds);

    // Enviar notificações para RESPONSÁVEIS (exceto quem já foi mencionado)
    for (const responsavelId of responsaveisIds) {
      if (responsavelId === autorId) {
        console.log("⏭️ Pulando autor nos responsáveis:", responsavelId);
        continue;
      }
      if (destinatariosProcessados.has(responsavelId)) {
        console.log("⏭️ Responsável já foi mencionado:", responsavelId);
        continue;
      }

      console.log("➕ Adicionando notificação de RESPONSÁVEL para:", responsavelId);
      const mensagem = `${autorProfile?.nickname || autorProfile?.nome || "Alguém"} comentou em uma tarefa que você é responsável.`;
      
      notificacoesParaEnviar.push({
        user_id: responsavelId,
        remetente_id: autorId,
        nota_id: notaId,
        projeto_id: projetoAtual?.id || null,
        tipo: "comentario_na_tarefa",
        mensagem,
        lido: false,
      });

      destinatariosProcessados.add(responsavelId);
    }

    // Inserir todas as notificações de uma vez
    console.log("📤 Total de notificações a enviar:", notificacoesParaEnviar.length);
    console.log("📋 Destinatários finais:", Array.from(destinatariosProcessados));
    
    if (notificacoesParaEnviar.length > 0) {
      const { error: insertError } = await supabaseClient
        .from("notificacoes")
        .insert(notificacoesParaEnviar);
      
      if (insertError) {
        console.error("❌ Erro ao inserir notificações:", insertError);
      } else {
        console.log("✅ Notificações enviadas com sucesso!");
      }
    } else {
      console.log("ℹ️ Nenhuma notificação para enviar");
    }

  } catch (err) {
    console.error("❌ Erro geral ao enviar notificações:", err);
  }
};

const carregarReacoesPorComentario = async (comentarioId, supabaseClient) => {
  const { data, error } = await supabaseClient
    .from("reacoes")
    .select("user_id, emoji")
    .eq("comentario_id", comentarioId);
  if (error) {
    console.error("Erro ao carregar reações:", error);
    return [];
  }
  return data;
};

const toggleReacao = async ({ comentarioId, userId, emoji, supabaseClient }) => {
  const { data: reacoesExistentes, error: checkError } = await supabaseClient
    .from("reacoes")
    .select("id")
    .eq("comentario_id", comentarioId)
    .eq("user_id", userId)
    .eq("emoji", emoji);

  if (checkError) {
    console.error("Erro ao verificar reação existente:", checkError);
    return "erro";
  }

  if (reacoesExistentes?.length > 0) {
    const { error } = await supabaseClient
      .from("reacoes")
      .delete()
      .eq("comentario_id", comentarioId)
      .eq("user_id", userId)
      .eq("emoji", emoji);
    if (error) {
      console.error("Erro ao remover reação:", error);
      return "erro";
    }
    return "removida";
  } else {
    const { error } = await supabaseClient
      .from("reacoes")
      .insert({ comentario_id: comentarioId, user_id: userId, emoji });
    if (error) {
      console.error("Erro ao adicionar reação:", error);
      return "erro";
    }
    return "adicionada";
  }
};

const TaskComentarios = ({ notaId, userId, userProfile, projetoAtual, containerId, supabaseClient, setImagemAmpliada }) => {
  const [comentario, setComentario] = useState("");
  const [comentarios, setComentarios] = useState([]);
  const [sugestoesMencoes, setSugestoesMencoes] = useState([]);
  const [comentando, setComentando] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingExcluir, setLoadingExcluir] = useState(false);
  const [menuAberto, setMenuAberto] = useState(null);
  const [respondendoA, setRespondendoA] = useState(null);
  const textareaRef = useRef(null);
  const respostaTextareaRef = useRef(null);
  const [containerIdDaNota, setContainerIdDaNota] = useState(containerId);
  const [nomeTarefa, setNomeTarefa] = useState("");
  
  // Novos estados para anexos
  const [anexosPendentes, setAnexosPendentes] = useState([]);
  const [uploadingAnexos, setUploadingAnexos] = useState([]);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    if (!notaId) return;
    const fetchNotaContainer = async () => {
      const { data, error } = await supabaseClient
        .from("notas")
        .select("container_id, nome, name")
        .eq("id", notaId)
        .single();
      if (!error && data) {
        if (data.container_id) setContainerIdDaNota(data.container_id);
        setNomeTarefa(data.nome || data.name || "Sem nome");
      }
    };
    fetchNotaContainer();
  }, [notaId, supabaseClient]);

  const formatarDataComentario = (dateString) => {
    const date = new Date(dateString);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    const isSameDay = (d1, d2) =>
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear();
    const hora = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    if (isSameDay(date, hoje)) return `Hoje às ${hora}`;
    if (isSameDay(date, ontem)) return `Ontem às ${hora}`;
    return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()} às ${hora}`;
  };

  const podeEditarComentario = (createdAt, autorId) => {
    if (autorId !== userId) return false;
    const agora = new Date();
    const criadoEm = new Date(createdAt);
    const diffMin = (agora - criadoEm) / (1000 * 60);
    return diffMin < 60;
  };

  // Função genérica de seleção de arquivos
  const handleSelecionarArquivos = (e, tipo, comentarioPaiId = null) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const novos = files.map(f => ({
      tempId: crypto.randomUUID(),
      file: f,
      fileName: f.name,
      isImage: f.type.startsWith('image/'),
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));

    if (comentarioPaiId) {
      setComentarios(prev => prev.map(c =>
        c.id === comentarioPaiId
          ? { ...c, respostaAnexosPendentes: [...(c.respostaAnexosPendentes || []), ...novos] }
          : c
      ));
    } else {
      setAnexosPendentes(prev => [...prev, ...novos]);
    }

    e.target.value = "";
  };

  // Função de upload de anexos
  const enviarAnexosDoComentario = async (comentarioId, listaPendentes) => {
    for (const item of listaPendentes) {
      try {
        const safeName = sanitizeFileName(item.fileName);
        const path = `comentarios/${comentarioId}_${Date.now()}_${safeName}`;

        const { error: uploadError } = await supabaseClient.storage
          .from("ata-anexos")
          .upload(path, item.file);
        if (uploadError) throw uploadError;

        const { data: pub } = supabaseClient.storage
          .from("ata-anexos")
          .getPublicUrl(path);

        const { data: inserted, error: insertError } = await supabaseClient
          .from("comentario_anexos")
          .insert({
            comentario_id: comentarioId,
            nota_id: notaId,
            user_id: userId,
            file_name: item.fileName,
            file_url: pub.publicUrl,
            mime_type: item.file.type || null,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        // atualiza o comentário local com o anexo recém-criado
        setComentarios(prev => prev.map(c =>
          c.id === comentarioId
            ? { ...c, anexos: [...(c.anexos || []), inserted] }
            : c
        ));
      } catch (err) {
        console.error("Erro ao enviar anexo do comentário:", err);
      }
    }
  };

  // Função para remover anexo do comentário
  const handleRemoverAnexoComentario = async (comentarioId, anexoId, fileUrl) => {
    if (!window.confirm("Deseja realmente excluir este anexo?")) return;
    try {
      // Extrair o caminho do arquivo da URL
      const url = new URL(fileUrl);
      const partes = url.pathname.split("/");
      const fileName = partes.slice(partes.indexOf("comentarios")).join("/");

      await supabaseClient.storage.from("ata-anexos").remove([fileName]);
      await supabaseClient
        .from("comentario_anexos")
        .delete()
        .eq("id", anexoId)
        .eq("user_id", userId);

      setComentarios(prev => prev.map(c => {
        if (c.id !== comentarioId) return c;
        return { ...c, anexos: (c.anexos || []).filter(a => a.id !== anexoId) };
      }));
    } catch (err) {
      console.error("Erro ao excluir anexo do comentário:", err);
      alert("Erro ao excluir anexo.");
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchComentarios = async () => {
      setLoading(true);
      try {
        const { data: comentariosData } = await supabaseClient
          .from("comentarios")
          .select("id, conteudo, created_at, user_id, agendado_por, respondendo_a")
          .eq("nota_id", notaId)
          .order("created_at", { ascending: false });

        if (comentariosData?.length > 0) {
          const userIds = [...new Set(comentariosData.map((c) => c.user_id))];
          const { data: profiles } = await supabaseClient
            .from("profiles")
            .select("id, nome, nickname, avatar_url")
            .in("id", userIds);
          const profileMap = {};
          profiles?.forEach((p) => (profileMap[p.id] = p));

          const comentariosComUsuario = await Promise.all(
            comentariosData.map(async (c) => {
              const reacoesRaw = await carregarReacoesPorComentario(c.id, supabaseClient);
              const reacoes = {};
              reacoesRaw.forEach((r) => {
                reacoes[r.emoji] = (reacoes[r.emoji] || 0) + 1;
              });

              const userIdsReacoes = [...new Set(reacoesRaw.map(r => r.user_id))];
              const { data: profilesReacoes } = await supabaseClient
                .from("profiles")
                .select("id, nome, nickname, avatar_url")
                .in("id", userIdsReacoes);

              const profileMapReacoes = {};
              profilesReacoes?.forEach(p => {
                profileMapReacoes[p.id] = p;
              });

              const reacoesDetalhadas = reacoesRaw.map(r => ({
                user_id: r.user_id,
                emoji: r.emoji,
                user_name: (profileMapReacoes[r.user_id]?.nickname || profileMapReacoes[r.user_id]?.nome || "Usuário"),
                avatar_url: profileMapReacoes[r.user_id]?.avatar_url || null,
              }));

              // Carregar anexos do comentário
              const { data: anexosData } = await supabaseClient
                .from("comentario_anexos")
                .select("*")
                .eq("comentario_id", c.id);

              return {
                ...c,
                profiles: profileMap[c.user_id] || { nome: "Usuário", nickname: null, avatar_url: null },
                formattedDate: formatarDataComentario(c.created_at),
                isEditing: false,
                editValue: undefined,
                mencionaUsuarioLogado: mencionaUsuario(c.conteudo, userProfile),
                estaAgendadoPeloUsuario: c.agendado_por === userId,
                ehResposta: !!c.respondendo_a,
                reacoes,
                reacoesDetalhadas,
                anexos: anexosData || [],
              };
            })
          );

          if (isMounted) setComentarios(comentariosComUsuario);
        } else if (isMounted) {
          setComentarios([]);
        }
      } catch (err) {
        console.error("Erro ao carregar comentários:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchComentarios();
    return () => { isMounted = false; };
  }, [notaId, userId, userProfile, supabaseClient]);

  const handleComentarioChange = (e) => {
    const valor = e.target.value;
    setComentario(valor);
    const cursor = e.target.selectionStart;
    const textoAteCursor = valor.slice(0, cursor);
    const match = textoAteCursor.match(/@([\p{L}\p{N}_-]*)$/u);
    if (match?.[1] && containerIdDaNota) {
      const termo = match[1];
      if (termo.length >= 1) {
        supabaseClient
          .from("convites")
          .select("user_id")
          .eq("container_id", containerIdDaNota)
          .eq("status", "aceito")
          .then(({ data: convitesAceitos, error }) => {
            if (error) {
              console.error("Erro ao buscar convites:", error);
              setSugestoesMencoes([]);
              return;
            }
            const userIdsDoContainer = convitesAceitos.map(c => c.user_id);
            if (userIdsDoContainer.length === 0) {
              setSugestoesMencoes([]);
              return;
            }
            supabaseClient
              .from("profiles")
              .select("id, nome, nickname, avatar_url")
              .in("id", userIdsDoContainer)
              .ilike("nickname", `%${termo}%`)
              .or(`nome.ilike.%${termo}%`)
              .limit(5)
              .then(({ data }) => {
                setSugestoesMencoes(data || []);
              });
          });
      } else {
        setSugestoesMencoes([]);
      }
    } else {
      setSugestoesMencoes([]);
    }
  };

  const inserirMencoes = (usuario) => {
    const textarea = respondendoA ? respostaTextareaRef.current : textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const valorAtual = respondendoA
      ? comentarios.find(c => c.id === respondendoA)?.respostaTemp || ""
      : comentario;
    const textoAntes = valorAtual.slice(0, cursorPos);
    const textoDepois = valorAtual.slice(cursorPos);
    const nomeParaMencoes = usuario.nickname || usuario.nome;
    const novoTextoAntes = textoAntes.replace(/@[\p{L}\p{N}_-]*$/u, `@${nomeParaMencoes}`);
    const novoTexto = novoTextoAntes + " " + textoDepois;

    if (respondendoA) {
      setComentarios(prev =>
        prev.map(c => (c.id === respondendoA ? { ...c, respostaTemp: novoTexto } : c))
      );
    } else {
      setComentario(novoTexto);
    }
    setSugestoesMencoes([]);
    setTimeout(() => {
      const novaPos = novoTextoAntes.length + 1;
      textarea.focus();
      textarea.setSelectionRange(novaPos, novaPos);
    }, 0);
  };

  const iniciarResposta = (comentarioId) => {
    setComentarios(prev =>
      prev.map(c =>
        c.id === comentarioId
          ? { ...c, respostaTemp: "", respostaAnexosPendentes: [], respostaAtiva: true }
          : { ...c, respostaAtiva: false }
      )
    );
    setRespondendoA(comentarioId);
  };

  const enviarResposta = async (comentarioPaiId) => {
    const comentarioPai = comentarios.find(c => c.id === comentarioPaiId);
    if (!comentarioPai || !comentarioPai.respostaTemp?.trim()) return;
    setComentando(true);
    try {
      const { data: novoComentario, error: insertError } = await supabaseClient
        .from("comentarios")
        .insert({
          nota_id: notaId,
          user_id: userId,
          conteudo: comentarioPai.respostaTemp.trim(),
          respondendo_a: comentarioPaiId,
        })
        .select('id, nota_id, user_id, conteudo, created_at, agendado_por, respondendo_a')
        .single();

      if (insertError) throw insertError;

      const { id, created_at } = novoComentario;
      const novoComentarioLocal = {
        id,
        nota_id: notaId,
        user_id: userId,
        conteudo: novoComentario.conteudo,
        created_at,
        agendado_por: null,
        respondendo_a: comentarioPaiId,
        profiles: userProfile || { nome: "Você", nickname: null, avatar_url: null },
        formattedDate: formatarDataComentario(created_at),
        isEditing: false,
        editValue: undefined,
        mencionaUsuarioLogado: mencionaUsuario(novoComentario.conteudo, userProfile),
        estaAgendadoPeloUsuario: false,
        ehResposta: true,
        reacoes: {},
        reacoesDetalhadas: [],
        anexos: [],
      };

      setComentarios(prev => [novoComentarioLocal, ...prev]);

      // Upload de anexos da resposta
      if (comentarioPai.respostaAnexosPendentes?.length > 0) {
        await enviarAnexosDoComentario(id, comentarioPai.respostaAnexosPendentes);
      }

      // ✅ Enviar notificações usando a nova função
      await notificarPessoasEnvolvidasNaTarefa({
        conteudo: novoComentario.conteudo,
        notaId,
        autorId: userId,
        autorProfile: userProfile,
        projetoAtual,
        nomeTarefa,
        supabaseClient,
      });

      setComentarios(prev =>
        prev.map(c => c.id === comentarioPaiId ? { ...c, respostaAtiva: false, respostaTemp: undefined, respostaAnexosPendentes: [] } : c)
      );
      setRespondendoA(null);
    } catch (err) {
      console.error("Erro ao enviar resposta:", err);
      alert("Erro ao enviar resposta.");
    } finally {
      setComentando(false);
    }
  };

  const cancelarResposta = (comentarioId) => {
    setComentarios(prev =>
      prev.map(c => c.id === comentarioId ? { ...c, respostaAtiva: false, respostaTemp: undefined, respostaAnexosPendentes: [] } : c)
    );
    setRespondendoA(null);
  };

  const handleAddComentario = async () => {
    if (!comentario.trim()) return;
    setComentando(true);
    try {
      const { data: novoComentario, error: insertError } = await supabaseClient
        .from("comentarios")
        .insert({
          nota_id: notaId,
          user_id: userId,
          conteudo: comentario.trim(),
          respondendo_a: null,
        })
        .select('id, nota_id, user_id, conteudo, created_at, agendado_por, respondendo_a')
        .single();

      if (insertError) throw insertError;

      const { id, created_at } = novoComentario;
      const novoComentarioLocal = {
        id,
        nota_id: notaId,
        user_id: userId,
        conteudo: novoComentario.conteudo,
        created_at,
        agendado_por: null,
        respondendo_a: null,
        profiles: userProfile || { nome: "Você", nickname: null, avatar_url: null },
        formattedDate: formatarDataComentario(created_at),
        isEditing: false,
        editValue: undefined,
        mencionaUsuarioLogado: mencionaUsuario(novoComentario.conteudo, userProfile),
        estaAgendadoPeloUsuario: false,
        ehResposta: false,
        reacoes: {},
        reacoesDetalhadas: [],
        anexos: [],
      };

      setComentarios(prev => [novoComentarioLocal, ...prev]);
      setComentario("");
      setSugestoesMencoes([]);

      // Upload de anexos pendentes
      if (anexosPendentes.length > 0) {
        await enviarAnexosDoComentario(id, anexosPendentes);
        setAnexosPendentes([]);
      }

      // ✅ Enviar notificações usando a nova função
      await notificarPessoasEnvolvidasNaTarefa({
        conteudo: novoComentario.conteudo,
        notaId,
        autorId: userId,
        autorProfile: userProfile,
        projetoAtual,
        nomeTarefa,
        supabaseClient,
      });

    } catch (err) {
      console.error("Erro ao salvar comentário:", err);
      alert("Erro ao salvar comentário.");
    } finally {
      setComentando(false);
    }
  };

  const handleEnviarParaAgenda = async (comentarioId) => {
    if (!comentarioId || typeof comentarioId !== 'string') return;
    const { data, error } = await supabaseClient
      .from("comentarios")
      .update({ agendado_por: userId })
      .eq("id", comentarioId)
      .select("agendado_por");
    if (error) {
      alert("Erro ao adicionar à agenda: " + (error.message || "tente novamente"));
    } else if (data?.[0]?.agendado_por === userId) {
      setComentarios(prev =>
        prev.map(c => c.id === comentarioId ? { ...c, estaAgendadoPeloUsuario: true } : c)
      );
      alert("Adicionado à agenda!");
    } else {
      alert("Não foi possível adicionar à agenda.");
    }
  };

  const handleSaveEdit = async (comentarioId) => {
    const alvo = comentarios.find((c) => c.id === comentarioId);
    const novoConteudo = (alvo?.editValue !== undefined ? alvo.editValue : alvo?.conteudo) || "";
    if (!novoConteudo.trim()) return;
    setLoading(true);
    try {
      await supabaseClient
        .from("comentarios")
        .update({ conteudo: novoConteudo.trim() })
        .eq("id", comentarioId)
        .eq("user_id", userId);
      setComentarios(prev =>
        prev.map(c =>
          c.id === comentarioId
            ? {
                ...c,
                conteudo: novoConteudo.trim(),
                isEditing: false,
                editValue: undefined,
                mencionaUsuarioLogado: mencionaUsuario(novoConteudo.trim(), userProfile),
                reacoes: c.reacoes,
                reacoesDetalhadas: c.reacoesDetalhadas,
              }
            : c
        )
      );
    } catch (err) {
      console.error("Erro ao editar comentário:", err);
      alert("Erro ao editar comentário.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (comentarioId) => {
    setComentarios(prev =>
      prev.map(c => (c.id === comentarioId ? { ...c, isEditing: true, editValue: c.conteudo } : c))
    );
    setMenuAberto(null);
  };

  const handleCancelEdit = (comentarioId) => {
    setComentarios(prev =>
      prev.map(c => (c.id === comentarioId ? { ...c, isEditing: false, editValue: undefined } : c))
    );
    setMenuAberto(null);
  };

  const handleExcluirComentario = async (comentarioId) => {
    setLoadingExcluir(true);
    try {
      await supabaseClient
        .from("comentarios")
        .delete()
        .eq("id", comentarioId)
        .eq("user_id", userId);
      setComentarios(prev => prev.filter(c => c.id !== comentarioId));
      setMenuAberto(null);
    } catch (err) {
      console.error("Erro ao excluir comentário:", err);
      alert("Erro ao excluir comentário.");
    } finally {
      setLoadingExcluir(false);
    }
  };

  const handleReacaoClick = async (comentarioId, emoji) => {
    const resultado = await toggleReacao({ comentarioId, userId, emoji, supabaseClient });
    if (resultado === "erro") {
      alert("Erro ao processar reação.");
      return;
    }
    setComentarios(prev =>
      prev.map(c => {
        if (c.id !== comentarioId) return c;
        const novasReacoes = { ...c.reacoes };
        if (resultado === "adicionada") {
          novasReacoes[emoji] = (novasReacoes[emoji] || 0) + 1;
        } else if (resultado === "removida") {
          if (novasReacoes[emoji] <= 1) {
            delete novasReacoes[emoji];
          } else {
            novasReacoes[emoji] -= 1;
          }
        }
        return { ...c, reacoes: novasReacoes };
      })
    );
  };

  const agruparComentarios = () => {
    const todos = [...comentarios];
    const principais = todos.filter(c => !c.ehResposta);
    const respostas = todos.filter(c => c.ehResposta);
    const mapaRespostas = {};
    respostas.forEach(r => {
      if (!mapaRespostas[r.respondendo_a]) {
        mapaRespostas[r.respondendo_a] = [];
      }
      mapaRespostas[r.respondendo_a].push(r);
    });
    Object.keys(mapaRespostas).forEach(paiId => {
      mapaRespostas[paiId].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });
    const resultado = [];
    principais.forEach(pai => {
      resultado.push({ ...pai, respostasFilhas: mapaRespostas[pai.id] || [] });
    });
    return resultado;
  };

  const perfilesPorId = {};
  comentarios.forEach(c => {
    if (c.profiles?.id) perfilesPorId[c.profiles.id] = c.profiles;
  });
  const comentariosAgrupados = agruparComentarios();

  return (
    <div className="comentarios-section">
      <h3>Comentários e atividades</h3>
      <div style={{ position: "relative" }}>
        <textarea
          ref={textareaRef}
          placeholder="Escrever um comentário... (use @ para mencionar, *bold* ou _itálico_)"
          value={comentario}
          onChange={handleComentarioChange}
          rows={3}
          disabled={loading || respondendoA}
        />
        {sugestoesMencoes.length > 0 && (
          <div
            className="sugestoes-list"
            style={{
              position: "absolute",
              zIndex: 10,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
              marginTop: "4px",
              width: "250px",
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {sugestoesMencoes.map((u) => {
              const nomeExibicao = u.nickname || u.nome;
              return (
                <div
                  key={u.id}
                  onClick={() => inserirMencoes(u)}
                  style={{
                    padding: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {u.avatar_url ? (
                    <img
                      src={u.avatar_url}
                      alt={nomeExibicao}
                      style={{ width: "24px", height: "24px", borderRadius: "50%" }}
                    />
                  ) : (
                    <FiUser style={{ width: "24px", height: "24px" }} />
                  )}
                  <span>{nomeExibicao}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Preview de anexos pendentes do comentário principal */}
      {anexosPendentes.length > 0 && (
        <div className="imagens-miniaturas" style={{ marginTop: 6 }}>
          {anexosPendentes.map(item => (
            <div key={item.tempId} className="miniatura-item">
              {item.isImage ? (
                <img src={item.previewUrl} alt={item.fileName} />
              ) : (
                <div className="anexo-item">{item.fileName}</div>
              )}
              <button
                className="botao-lixeira-miniatura"
                onClick={() => setAnexosPendentes(prev => prev.filter(a => a.tempId !== item.tempId))}
              >
                <FaTrashAlt size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      
      <div className="coment-toolbar">
        <div className="coment-toolbar-icons">
          <button
            type="button"
            title="Mencionar"
            onClick={() => { setComentario(prev => prev + "@"); textareaRef.current?.focus(); }}
          >
            @
          </button>
          <button
            type="button"
            title="Negrito"
            onClick={() => { setComentario(prev => prev + "**"); textareaRef.current?.focus(); }}
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            title="Itálico"
            onClick={() => { setComentario(prev => prev + "_"); textareaRef.current?.focus(); }}
          >
            <em>I</em>
          </button>
          <button type="button" title="Emoji"><FaSmile size={14} /></button>
          <button type="button" title="Anexar imagem" onClick={() => imageInputRef.current?.click()}>
            <FaCamera size={14} />
          </button>
          <input
            type="file"
            ref={imageInputRef}
            hidden
            multiple
            accept="image/*"
            onChange={(e) => handleSelecionarArquivos(e, 'imagem')}
          />
          <button type="button" title="Anexar arquivo" onClick={() => fileInputRef.current?.click()}>
            <FaPaperclip size={14} />
          </button>
          <input
            type="file"
            ref={fileInputRef}
            hidden
            multiple
            onChange={(e) => handleSelecionarArquivos(e, 'arquivo')}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            type="button"
            className="coment-btn"
            onClick={handleAddComentario}
            disabled={loading || comentando || (!comentario.trim() && anexosPendentes.length === 0) || respondendoA}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
            Comentar
          </button>
          {(comentando || loadingExcluir) && <span className="loader"></span>}
        </div>
      </div>
      
      {/* LISTA DE COMENTÁRIOS COM SKELETON */}
      {loading && comentarios.length === 0 ? (
        <ComentariosSkeleton />
      ) : (
        <div className="comentarios-lista">
          {comentariosAgrupados.map((pai) => {
            const profile = pai.profiles || { nome: "Usuário", nickname: null, avatar_url: null };
            const nomeExibicao = profile.nickname || profile.nome;
            const editavel = podeEditarComentario(pai.created_at, pai.user_id);
            const podeEnviarParaAgenda = pai.mencionaUsuarioLogado && !pai.estaAgendadoPeloUsuario;

            return (
              <React.Fragment key={pai.id}>
                <div className="comentario-item">
                  <div className="comentario-avatar">
                    {profile.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt={nomeExibicao}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "block";
                        }}
                      />
                    ) : (
                      <FiUser className="avatar-placeholder" />
                    )}
                  </div>
                  <div className="comentario-conteudo">
                    <div className="comentario-header">
                      <div className="comentario-autor">
                        <strong>{nomeExibicao}</strong>
                      </div>
                      <div className="comentario-meta" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <button
                          type="button"
                          title={`Responder a ${nomeExibicao}`}
                          onClick={() => iniciarResposta(pai.id)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#666",
                            fontSize: "16px",
                            display: "flex",
                            alignItems: "center",
                          }}
                          aria-label={`Responder a ${nomeExibicao}`}
                        >
                          <IoReturnUpBack />
                        </button>
                        <span>{pai.formattedDate}</span>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <TaskReactions
                            comentarioId={pai.id}
                            userId={userId}
                            reacoes={pai.reacoes}
                            reacoesDetalhadas={pai.reacoesDetalhadas}
                            onToggleReacao={handleReacaoClick}
                            disabled={loading || comentando}
                          />
                          {podeEnviarParaAgenda && (
                            <button
                              type="button"
                              title="Adicionar à minha agenda"
                              onClick={() => handleEnviarParaAgenda(pai.id)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#1E88E5", fontSize: "16px" }}
                              aria-label="Adicionar à agenda"
                            >
                              <FiCalendar />
                            </button>
                          )}
                          {editavel && !pai.isEditing && (
                            <button
                              type="button"
                              className="comentario-menu-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuAberto(menuAberto === pai.id ? null : pai.id);
                              }}
                              aria-label="Opções"
                            >
                              ⋮
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {pai.isEditing ? (
                      <div>
                        <textarea
                          value={pai.editValue !== undefined ? pai.editValue : pai.conteudo}
                          onChange={(e) =>
                            setComentarios(prev =>
                              prev.map(x => (x.id === pai.id ? { ...x, editValue: e.target.value } : x))
                            )
                          }
                          rows={3}
                          style={{ width: "100%", resize: "none", marginTop: "4px" }}
                        />
                        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                          <button type="button" onClick={() => handleSaveEdit(pai.id)} disabled={loading}>
                            Salvar
                          </button>
                          <button type="button" onClick={() => handleCancelEdit(pai.id)} disabled={loading}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Imagens ACIMA do texto */}
                        {pai.anexos?.length > 0 && (
                          <ComentarioImagens
                            anexos={pai.anexos}
                            userId={userId}
                            onRemover={(anexoId, fileUrl) => handleRemoverAnexoComentario(pai.id, anexoId, fileUrl)}
                            setImagemAmpliada={setImagemAmpliada}
                          />
                        )}
                        
                        <div className="comentario-texto">
                          {renderTextoFormatadoComMencoes(pai.conteudo, perfilesPorId, projetoAtual, containerIdDaNota, supabaseClient)}
                        </div>
                        
                        {/* Arquivos (não-imagem) ABAIXO do texto */}
                        {pai.anexos?.length > 0 && (
                          <ComentarioArquivos
                            anexos={pai.anexos}
                            userId={userId}
                            onRemover={(anexoId, fileUrl) => handleRemoverAnexoComentario(pai.id, anexoId, fileUrl)}
                          />
                        )}
                      </>
                    )}
                    {menuAberto === pai.id && editavel && !pai.isEditing && (
                      <div className="comentario-menu">
                        <button type="button" onClick={() => handleStartEdit(pai.id)}>
                          Editar
                        </button>
                        <button type="button" onClick={() => handleExcluirComentario(pai.id)}>
                          Excluir
                        </button>
                      </div>
                    )}
                    {pai.respostaAtiva && (
                      <div style={{ marginTop: "12px", marginLeft: "40px", width: "calc(100% - 40px)" }}>
                        <textarea
                          ref={respostaTextareaRef}
                          placeholder="Escreva sua resposta..."
                          value={pai.respostaTemp || ""}
                          onChange={(e) =>
                            setComentarios(prev =>
                              prev.map(x =>
                                x.id === pai.id ? { ...x, respostaTemp: e.target.value } : x
                              )
                            )
                          }
                          rows={2}
                          style={{
                            width: "100%",
                            fontSize: "14px",
                            padding: "6px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            resize: "none",
                          }}
                        />
                        {/* Preview de anexos pendentes da resposta */}
                        {pai.respostaAnexosPendentes?.length > 0 && (
                          <div className="imagens-miniaturas" style={{ marginTop: 6 }}>
                            {pai.respostaAnexosPendentes.map(item => (
                              <div key={item.tempId} className="miniatura-item">
                                {item.isImage ? (
                                  <img src={item.previewUrl} alt={item.fileName} />
                                ) : (
                                  <div className="anexo-item">{item.fileName}</div>
                                )}
                                <button
                                  className="botao-lixeira-miniatura"
                                  onClick={() => setComentarios(prev =>
                                    prev.map(c =>
                                      c.id === pai.id
                                        ? { ...c, respostaAnexosPendentes: c.respostaAnexosPendentes.filter(a => a.tempId !== item.tempId) }
                                        : c
                                    )
                                  )}
                                >
                                  <FaTrashAlt size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="coment-toolbar" style={{ marginTop: "6px" }}>
                          <div className="coment-toolbar-icons">
                            <button type="button" title="Anexar imagem" onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.multiple = true;
                              input.accept = 'image/*';
                              input.onchange = (e) => handleSelecionarArquivos(e, 'imagem', pai.id);
                              input.click();
                            }}>
                              📷
                            </button>
                            <button type="button" title="Anexar arquivo" onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.multiple = true;
                              input.onchange = (e) => handleSelecionarArquivos(e, 'arquivo', pai.id);
                              input.click();
                            }}>
                              📎
                            </button>
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              type="button"
                              className="coment-btn"
                              onClick={() => enviarResposta(pai.id)}
                              disabled={(!pai.respostaTemp?.trim() && (!pai.respostaAnexosPendentes || pai.respostaAnexosPendentes.length === 0))}
                            >
                              Responder
                            </button>
                            <button
                              type="button"
                              onClick={() => cancelarResposta(pai.id)}
                              style={{ background: "none", color: "#666" }}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {pai.respostasFilhas.map((resposta) => {
                  const respProfile = resposta.profiles || { nome: "Usuário", nickname: null, avatar_url: null };
                  const respNome = respProfile.nickname || respProfile.nome;
                  return (
                    <div key={resposta.id} className="comentario-item comentario-resposta">
                      <div className="comentario-avatar">
                        {respProfile.avatar_url ? (
                          <img
                            src={respProfile.avatar_url}
                            alt={respNome}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "block";
                            }}
                          />
                        ) : (
                          <FiUser className="avatar-placeholder" />
                        )}
                      </div>
                      <div className="comentario-conteudo">
                        <div className="comentario-header">
                          <div className="comentario-autor">
                            <strong>{respNome}</strong>
                          </div>
                          <div className="comentario-meta" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "0.9em", color: "#888" }}>{resposta.formattedDate}</span>
                            <TaskReactions
                              comentarioId={resposta.id}
                              userId={userId}
                              reacoes={resposta.reacoes}
                              reacoesDetalhadas={resposta.reacoesDetalhadas}
                              onToggleReacao={handleReacaoClick}
                              disabled={loading || comentando}
                            />
                          </div>
                        </div>
                        {/* Imagens da resposta ACIMA do texto */}
                        {resposta.anexos?.length > 0 && (
                          <ComentarioImagens
                            anexos={resposta.anexos}
                            userId={userId}
                            onRemover={(anexoId, fileUrl) => handleRemoverAnexoComentario(resposta.id, anexoId, fileUrl)}
                            setImagemAmpliada={setImagemAmpliada}
                          />
                        )}
                        
                        <div className="comentario-texto">
                          {renderTextoFormatadoComMencoes(resposta.conteudo, perfilesPorId, projetoAtual, containerIdDaNota, supabaseClient)}
                        </div>
                        
                        {/* Arquivos da resposta (não-imagem) ABAIXO do texto */}
                        {resposta.anexos?.length > 0 && (
                          <ComentarioArquivos
                            anexos={resposta.anexos}
                            userId={userId}
                            onRemover={(anexoId, fileUrl) => handleRemoverAnexoComentario(resposta.id, anexoId, fileUrl)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TaskComentarios;